import { LOCAL_STORAGE_KEYS, getLocalStorageValue } from '@/constants/localStorage'

export function useLocalStorage() {
  // Storage event handler
  const handleStorageUpdate = (event: StorageEvent, context: any) => {
    if (event.newValue === event.oldValue) {
      return
    }
    
    switch (event.key) {
      case LOCAL_STORAGE_KEYS.THEME:
        context.$store.dispatch('profileSetTheme', event.newValue)
        break
      case LOCAL_STORAGE_KEYS.ID_LOCALE:
        context.$store.dispatch('i18n/setLocale', event.newValue)
        break
      case LOCAL_STORAGE_KEYS.ID_LANGUAGE:
        context.$store.dispatch('i18n/setLanguage', event.newValue)
        break
      case LOCAL_STORAGE_KEYS.THEME_DO_NOT_SHOW:
        context.$store.dispatch('theme/setThemeDoNotShow', event.newValue === 'true')
        break
      case LOCAL_STORAGE_KEYS.LANGUAGE_SWITCHER_DO_NOT_SHOW:
        context.$store.dispatch('i18n/setLanguageSwitcherDoNotShow', event.newValue === 'true')
        break
      case LOCAL_STORAGE_KEYS.WS_SESSION:
        // Handle session change from another tab - generate new session and send authSession
        if (event.newValue && event.newValue !== event.oldValue) {
          context.$store.dispatch('connection/initSession');
          if (context.wsSend) {
            context.wsSend('authSession', {
              session: event.newValue,
            })
          }
        }
        break
      case LOCAL_STORAGE_KEYS.AUTH_MODE:
        context.setAuthMode(event.newValue)
        break
      case LOCAL_STORAGE_KEYS.LAST_TIME_BUILD:
        context.setLastTimeBuild(event.newValue)
        break
      case LOCAL_STORAGE_KEYS.SELECTED_TIMEFRAME:
        // TODO: handle that
        break
      case LOCAL_STORAGE_KEYS.SELECTED_CANDLE_COUNT:
        // TODO: handle that
        break
    }
  }
  
  // Initialize app state from localStorage
  const initializeFromStorage = (context: any) => {
    // Initialize timeframes and candle counts
    context.selectTimeFrame(
      context.timeframes.includes(getLocalStorageValue(LOCAL_STORAGE_KEYS.SELECTED_TIMEFRAME))
        ? getLocalStorageValue(LOCAL_STORAGE_KEYS.SELECTED_TIMEFRAME)
        : '1w'
    )
    
    context.selectCandleCount(
      context.candleCounts.includes(parseInt(getLocalStorageValue(LOCAL_STORAGE_KEYS.SELECTED_CANDLE_COUNT) || '0'))
        ? parseInt(getLocalStorageValue(LOCAL_STORAGE_KEYS.SELECTED_CANDLE_COUNT) || '100')
        : 100
    )
    
    // Initialize i18n settings
    context.$store.dispatch('i18n/setLocale', getLocalStorageValue(LOCAL_STORAGE_KEYS.ID_LOCALE) || null)
    context.$store.dispatch('i18n/setLanguage', getLocalStorageValue(LOCAL_STORAGE_KEYS.ID_LANGUAGE) || null)
    context.$store.dispatch('i18n/setLanguageSwitcherDoNotShow', getLocalStorageValue(LOCAL_STORAGE_KEYS.LANGUAGE_SWITCHER_DO_NOT_SHOW) === 'true')
    
    // Initialize theme settings
    context.$store.dispatch('theme/setTheme', getLocalStorageValue(LOCAL_STORAGE_KEYS.THEME) || 'auto')
    context.$store.dispatch('theme/setThemeDoNotShow', getLocalStorageValue(LOCAL_STORAGE_KEYS.THEME_DO_NOT_SHOW) === 'true')
    
    // Initialize auth mode
    context.setAuthMode(getLocalStorageValue(LOCAL_STORAGE_KEYS.AUTH_MODE) || null)
    
    // Initialize last time build
    const storageLastTimeBuild = getLocalStorageValue(LOCAL_STORAGE_KEYS.LAST_TIME_BUILD)
    context.setLastTimeBuild(storageLastTimeBuild === null ? null : parseInt(storageLastTimeBuild))
  }
  
  // Setup storage event listener
  const setupStorageListener = (context: any) => {
    const storageHandler = (event: StorageEvent) => handleStorageUpdate(event, context)
    
    window.addEventListener('storage', storageHandler)
    
    // Return cleanup function
    return () => {
      window.removeEventListener('storage', storageHandler)
    }
  }
  
  // Clear browser data function
  const clearBrowserData = (context: any) => {
    // Clear most localStorage keys, keeping LAST_TIME_BUILD as it's service info
    const keysToClear = [
      LOCAL_STORAGE_KEYS.THEME,
      LOCAL_STORAGE_KEYS.THEME_DO_NOT_SHOW,
      LOCAL_STORAGE_KEYS.ID_LOCALE,
      LOCAL_STORAGE_KEYS.ID_LANGUAGE,
      LOCAL_STORAGE_KEYS.LANGUAGE_SWITCHER_DO_NOT_SHOW,
      LOCAL_STORAGE_KEYS.AUTH_MODE,
      LOCAL_STORAGE_KEYS.SELECTED_TIMEFRAME,
      LOCAL_STORAGE_KEYS.SELECTED_CANDLE_COUNT,
    ]
    
    // Clear the specified keys
    keysToClear.forEach(key => {
      localStorage.removeItem(key)
    })
    
    // For WS_SESSION - generate new session instead of removing
    context.$store.dispatch('connection/initSession', true)
    
    // Send authSession with new session to re-authenticate gracefully
    if (context.wsSend) {
      context.wsSend('authSession', {
        session: context.$store.state.connection.wsSession,
      })
    }
  }

  return {
    handleStorageUpdate,
    initializeFromStorage,
    setupStorageListener,
    clearBrowserData,
  }
} 